import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import { formatDate } from "../../../core/api";
import { getSupportTicket, updateSupportTicket } from "../../../core/services/supportService";
import { hasPermission } from "../../../core/adminPermissions";

const SupportDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [reply, setReply] = useState("");
  const [status, setStatus] = useState("open");

  const loadTicket = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const result = await getSupportTicket(id);
      setTicket(result.ticket);
      setStatus(result.ticket.status);
      setReply(result.ticket.reply || "");
    } catch (error: any) {
      Swal.fire("Error", error.message || "Unable to load ticket", "error");
      navigate("/support");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    void loadTicket();
  }, [loadTicket]);

  const handleReply = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!reply.trim()) {
      Swal.fire("Message required", "Write a reply before sending.", "warning");
      return;
    }

    setSaving(true);
    try {
      await updateSupportTicket(id, {
        message: reply.trim(),
        status,
      });
      await loadTicket();
      Swal.fire({ title: "Reply Saved", icon: "success", timer: 1200, showConfirmButton: false });
    } catch (error: any) {
      Swal.fire("Failed", error.message || "Unable to send reply", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="page-section loading-state"><h2>Loading Support Request…</h2></div>;
  }

  if (!ticket) return null;

  return (
    <div className="detail-page">
      <div className="page-hdr">
        <div className="page-hdr-left">
          <Link to="/support" className="btn-sm btn-outline back-link">← Back to Support</Link>
          <div className="page-eyebrow">Support Request</div>
          <h1 className="page-title">{ticket.subject}</h1>
          <div className="page-sub">{ticket.category} · {formatDate(ticket.createdAt)}</div>
        </div>
        <div className="page-hdr-right">
          <span className={`status-badge ${ticket.status === "closed" ? "muted" : "warning"}`}>{ticket.status}</span>
        </div>
      </div>

      <div className="detail-grid-2">
        <section className="table-card info-card">
          <h3 className="info-card-title">Customer</h3>
          <div className="info-list">
            <p><strong>Name:</strong> {ticket.user?.name}</p>
            <p><strong>Phone:</strong> {ticket.user?.phone || "—"}</p>
            <p><strong>Email:</strong> {ticket.user?.email || "—"}</p>
            {ticket.user?._id && hasPermission("users") && (
              <p><Link to={`/users/${ticket.user._id}`} className="table-link">View Customer Profile</Link></p>
            )}
          </div>
        </section>

        <section className="table-card info-card">
          <h3 className="info-card-title">Customer Message</h3>
          <div className="info-list">
            <p>{ticket.message}</p>
            {ticket.repliedAt && <p className="text-muted-sm">Last reply: {formatDate(ticket.repliedAt)}</p>}
          </div>
        </section>
      </div>

      <section className="table-card">
        <div className="table-head">
          <h2>Admin Reply</h2>
          <p>Reply to customer and update status</p>
        </div>
        <form onSubmit={handleReply} className="admin-card-body admin-form-grid">
          <div className="form-field admin-status-field">
            <label className="modal-label">Reply Message</label>
            <textarea className="admin-input" rows={4} value={reply} onChange={(e) => setReply(e.target.value)} />
          </div>
          <div className="form-field">
            <label className="modal-label">Status</label>
            <select className="admin-input" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="open">Open</option>
              <option value="closed">Closed</option>
            </select>
          </div>
          <div className="form-field admin-status-field">
            <button type="submit" className="btn-sm btn-primary" disabled={saving}>
              {saving ? "Saving..." : "Save Reply"}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
};

export default SupportDetail;
